/**
 * The one path every API call takes. Adapted from Book&Host 69d7046
 * web/src/core/api.ts: the fetch wrapper, ApiError, the bearer token with
 * X-Session-Token renewal and the offline-write block carry over; the endpoint
 * methods are MasterDay's (doc 04).
 *
 * No user-facing text lives here: the screens choose it from `error.code`
 * (doc 07 §2). The server's `message` is English, for logs.
 */
import { getConfig } from './config';
import { notifyOfflineMutationBlocked } from './network';
import type {
  Appointment,
  AppointmentStatus,
  ChatMessage,
  ClientCard,
  ClientListItem,
  CurrentUser,
  DayHours,
  DayView,
  InboxPage,
  Locale,
  MonthDay,
  NewAppointment,
  Service,
  TimeOff,
  TodayView,
} from '../types';

export class ApiError extends Error {
  code: string;
  status: number;
  details?: Record<string, unknown>;

  constructor(message: string, code = 'API_ERROR', status = 500, details?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

const AUTH_TOKEN_KEY = 'masterday_auth_token';

export function getAuthToken(): string | null {
  try {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setAuthToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(AUTH_TOKEN_KEY, token);
    else localStorage.removeItem(AUTH_TOKEN_KEY);
  } catch {
    /* private mode: the session cookie still works */
  }
}

export async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const method = options.method?.toUpperCase() || 'GET';

  // Fail loudly on an offline write rather than pretend it saved.
  if (method !== 'GET' && typeof navigator !== 'undefined' && !navigator.onLine) {
    notifyOfflineMutationBlocked();
    throw new ApiError('offline', 'OFFLINE_MUTATION_BLOCKED', 0);
  }

  const { apiUrl } = getConfig();
  const url = `${apiUrl}${path.startsWith('/') ? path : `/${path}`}`;
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = getAuthToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  let response: Response;
  try {
    response = await fetch(url, {
      ...options,
      credentials: 'include',
      headers: { ...headers, ...(options.headers as Record<string, string> | undefined) },
    });
  } catch {
    throw new ApiError('network', 'NETWORK_ERROR', 0);
  }

  // The server slides the session forward on this header once the token is a
  // week old (doc 04 §1). A refreshed cookie alone would not renew a client
  // that authenticates with a bearer token from localStorage.
  const refreshed = response.headers.get('X-Session-Token');
  if (refreshed) setAuthToken(refreshed);

  if (response.status === 204) return undefined as T;

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = (data as { error?: { code?: string; message?: string; details?: Record<string, unknown> } }).error ?? {};
    throw new ApiError(
      err.message || response.statusText || 'error',
      err.code || 'HTTP_ERROR',
      response.status,
      err.details,
    );
  }
  return data as T;
}

const json = (body: unknown): RequestInit['body'] => JSON.stringify(body);

export const api = {
  // Auth (doc 04 §3)
  getMe: () => request<CurrentUser>('/auth/me'),
  async login(email: string, password: string): Promise<CurrentUser> {
    const me = await request<CurrentUser>('/auth/login', { method: 'POST', body: json({ email, password }) });
    if (me.token) setAuthToken(me.token);
    return me;
  },
  async register(payload: { name: string; email: string; password: string; locale: Locale }): Promise<CurrentUser> {
    const me = await request<CurrentUser>('/auth/register', { method: 'POST', body: json(payload) });
    if (me.token) setAuthToken(me.token);
    return me;
  },
  async logout(): Promise<void> {
    try {
      await request('/auth/logout', { method: 'POST' });
    } finally {
      setAuthToken(null);
    }
  },
  updateMe: (changes: Partial<Pick<CurrentUser, 'name' | 'locale' | 'notify_messages' | 'notify_appointments'>>) =>
    request<CurrentUser>('/auth/me', { method: 'PATCH', body: json(changes) }),
  /** D-89: erases the account and the salon. A wrong password is 403 WRONG_PASSWORD. */
  async deleteAccount(password: string): Promise<void> {
    await request('/auth/me', { method: 'DELETE', body: json({ password }) });
    setAuthToken(null);
  },

  // Calendar (doc 04 §4)
  today: () => request<TodayView>('/today'),
  day: (date: string) => request<DayView>(`/day?date=${encodeURIComponent(date)}`),
  week: (monday: string) => request<DayView[]>(`/week?from=${encodeURIComponent(monday)}`),
  month: (yyyyMm: string) => request<MonthDay[]>(`/month?month=${encodeURIComponent(yyyyMm)}`),

  // Appointments (doc 04 §5)
  getAppointment: (id: string) => request<Appointment>(`/appointments/${id}`),
  createAppointment: (body: NewAppointment) =>
    request<Appointment>('/appointments', { method: 'POST', body: json(body) }),
  updateAppointment: (
    id: string,
    changes: Partial<{ starts_at: string; ends_at: string; service_id: string; price: string; notes: string | null; status: AppointmentStatus; overlap_ack: boolean }>,
  ) => request<Appointment>(`/appointments/${id}`, { method: 'PATCH', body: json(changes) }),
  cancelAppointment: (id: string) => request<Appointment>(`/appointments/${id}`, { method: 'DELETE' }),

  // Services (doc 04 §6)
  services: () => request<Service[]>('/services'),
  createService: (body: Pick<Service, 'name_ru' | 'name_ky' | 'duration_minutes' | 'price'>) =>
    request<Service>('/services', { method: 'POST', body: json(body) }),
  updateService: (id: string, changes: Partial<Pick<Service, 'name_ru' | 'name_ky' | 'duration_minutes' | 'price'>>) =>
    request<Service>(`/services/${id}`, { method: 'PATCH', body: json(changes) }),
  /** Retires the service (`active = false`); past appointments keep its name (D-68). */
  deleteService: (id: string) => request<Service>(`/services/${id}`, { method: 'DELETE' }),

  // Working hours and time off (doc 04 §6)
  schedule: () => request<DayHours[]>('/schedule'),
  putSchedule: (week: DayHours[]) => request<DayHours[]>('/schedule', { method: 'PUT', body: json(week) }),
  timeOff: (from?: string) => request<TimeOff[]>(`/time-off${from ? `?from=${encodeURIComponent(from)}` : ''}`),
  createTimeOff: (body: { starts_at: string; ends_at: string; reason: string | null }) =>
    request<TimeOff>('/time-off', { method: 'POST', body: json(body) }),
  deleteTimeOff: (id: string) => request<void>(`/time-off/${id}`, { method: 'DELETE' }),

  // Clients (doc 04 §7)
  clients: (q?: string, limit?: number) => {
    const qs = [q && `q=${encodeURIComponent(q)}`, limit && `limit=${limit}`].filter(Boolean).join('&');
    return request<{ items: ClientListItem[]; next_cursor: string | null }>(`/clients${qs ? `?${qs}` : ''}`);
  },
  client: (id: string) => request<ClientCard>(`/clients/${id}`),
  createClient: (body: { name: string; phone?: string | null; notes?: string | null }) =>
    request<ClientCard>('/clients', { method: 'POST', body: json(body) }),
  updateClient: (id: string, changes: { name?: string; phone?: string | null; notes?: string | null }) =>
    request<ClientCard>(`/clients/${id}`, { method: 'PATCH', body: json(changes) }),

  // Messages (doc 04 §8)
  inbox: (channel?: string, q?: string) => {
    const qs = [channel && `channel=${channel}`, q && `q=${encodeURIComponent(q)}`, 'limit=200'].filter(Boolean).join('&');
    return request<InboxPage>(`/messages?${qs}`);
  },
  /** The tab badge on every screen (D-88): one number, not the inbox. */
  unreadCount: () => request<{ unread_total: number }>('/messages/unread-count'),
  thread: (clientId: string) =>
    request<{ items: ChatMessage[]; next_cursor: string | null }>(`/messages/${clientId}?limit=200`),
  reply: (clientId: string, body: string) =>
    request<ChatMessage>(`/messages/${clientId}`, { method: 'POST', body: json({ body }) }),
  markRead: (clientId: string) => request<void>(`/messages/${clientId}/read`, { method: 'POST' }),

  // Push (doc 05 §6): one row per phone she is signed in on.
  registerDeviceToken: (token: string, platform: 'ios' | 'android' | 'web') =>
    request<void>('/devices/token', { method: 'POST', body: json({ token, platform }) }),
  deleteDeviceToken: (token: string) =>
    request<void>(`/devices/token/${encodeURIComponent(token)}`, { method: 'DELETE' }),
};
